/*
 * Search drawer idle state — the two things shown before a query is typed:
 *
 *   <recent-searches>          the visitor's own search history (localStorage)
 *   <recently-viewed-search>   their last viewed products, fetched on first open
 *
 * Markup lives in snippets/recent-searches.liquid and
 * snippets/recently-viewed-search.liquid; the render target for the products is
 * sections/search-recent-products.liquid.
 *
 * An external file rather than inline script tags: the search drawer sits in the
 * overlay group, which is in the layout, so inlining would re-send ~7KB on every
 * page instead of letting the CDN cache it once. No Liquid in here for the same
 * reason — every merchant-facing string arrives as a data attribute.
 */

/* --- Recent searches ------------------------------------------------------ */
{
  const KEY = 'sneak:recent-searches';
  const LIMIT = 6;
  const MAX_LENGTH = 64;

  const read = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(KEY));
      return Array.isArray(parsed) ? parsed.filter((term) => typeof term === 'string') : [];
    } catch (e) {
      // Private mode, disabled storage, or a value something else wrote.
      return [];
    }
  };

  const write = (terms) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(terms.slice(0, LIMIT)));
    } catch (e) {
      // Quota or disabled storage — the pills just don't persist.
    }
    document.dispatchEvent(new CustomEvent('recent-searches:change'));
  };

  const record = (rawTerm) => {
    if (typeof rawTerm !== 'string') return;

    const term = rawTerm.replace(/\s+/g, ' ').trim().slice(0, MAX_LENGTH);
    if (term.length < 2) return;

    const kept = read().filter((existing) => existing.toLowerCase() !== term.toLowerCase());
    write([term, ...kept]);
  };

  class RecentSearches extends HTMLElement {
    connectedCallback() {
      this._list = this.querySelector('[data-recent-list]');
      this._clockIcon = this.querySelector('[data-recent-icon-clock]');
      this._closeIcon = this.querySelector('[data-recent-icon-close]');
      this._form = this.closest('form');
      this._input = this._form?.elements['q'];

      this.addEventListener('click', this._onClick.bind(this));
      document.addEventListener('recent-searches:change', () => this._render());

      // A predictive result leaves for a product, so the typed term is only
      // recordable at the moment of the click.
      this._form?.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');
        if (link?.closest('[slot="results"]')) record(this._input?.value);
      });

      // The drawer is in the DOM from page load, so re-read storage on each
      // open: another tab, or the results page itself, may have added terms.
      this.closest('search-drawer')?.addEventListener('dialog:after-show', () => this._render());

      this._render();
    }

    _render() {
      const terms = read().slice(0, LIMIT);

      this._list.replaceChildren(...terms.map((term) => this._pill(term)));
      this.hidden = terms.length === 0;
    }

    _pill(term) {
      const item = document.createElement('li');
      item.className = 'recent-searches__item';

      const search = document.createElement('button');
      search.type = 'button';
      search.className = 'recent-searches__term';
      search.dataset.recentTerm = term;
      if (this._clockIcon) search.appendChild(this._clockIcon.content.cloneNode(true));

      const label = document.createElement('span');
      label.textContent = term;
      search.appendChild(label);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'recent-searches__remove';
      remove.dataset.recentRemove = term;
      remove.setAttribute('aria-label', (this.dataset.removeLabel || '%s').replace('%s', term));
      if (this._closeIcon) remove.appendChild(this._closeIcon.content.cloneNode(true));

      item.append(search, remove);
      return item;
    }

    _onClick(event) {
      const removeButton = event.target.closest('[data-recent-remove]');

      if (removeButton) {
        const dropped = removeButton.dataset.recentRemove;
        write(read().filter((term) => term !== dropped));
        this._input?.focus();
        return;
      }

      if (event.target.closest('[data-recent-clear]')) {
        write([]);
        this._input?.focus();
        return;
      }

      const term = event.target.closest('[data-recent-term]')?.dataset.recentTerm;
      if (!term) return;

      if (!this._input) {
        window.location.href = `${this.dataset.searchUrl}?q=${encodeURIComponent(term)}&type=product`;
        return;
      }

      // Re-record so an old pill floats back to the front of the row.
      record(term);
      this._input.value = term;
      this._input.focus();
      this._input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  if (!window.customElements.get('recent-searches')) {
    window.customElements.define('recent-searches', RecentSearches);
  }

  // Every completed search ends on the results page, so read the term from the
  // URL rather than from a submit handler — shared links and back-navigation
  // count the same way.
  if (!window.__recentSearchesFromUrl) {
    window.__recentSearchesFromUrl = true;

    if (/\/search\/?$/.test(window.location.pathname)) {
      record(new URLSearchParams(window.location.search).get('q') || '');
    }
  }
}

/* --- Recently viewed products --------------------------------------------- */
{
  const KEY = 'theme:recently-viewed-products';
  const SECTION_ID = 'search-recent-products';

  class RecentlyViewedSearch extends HTMLElement {
    connectedCallback() {
      const drawer = this.closest('search-drawer');

      if (!drawer || drawer.hasAttribute('open')) {
        this._load();
        return;
      }

      // Watch the `open` attribute rather than waiting for dialog:after-show:
      // that event only fires once the drawer has finished animating, and the
      // request may as well be in flight during those ~300ms.
      const observer = new MutationObserver(() => {
        if (!drawer.hasAttribute('open')) return;
        observer.disconnect();
        this._load();
      });

      observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }

    get _query() {
      let ids;

      try {
        ids = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      } catch (e) {
        // Private mode or a value something else wrote.
        return '';
      }

      if (!Array.isArray(ids)) return '';

      const excluded = parseInt(this.dataset.excludeId, 10);

      return ids
        .filter((id) => Number.isFinite(id) && id !== excluded)
        .slice(0, parseInt(this.dataset.productsCount, 10) || 4)
        .map((id) => `id:${id}`)
        .join(' OR ');
    }

    async _load() {
      if (this._loaded) return;
      this._loaded = true;

      const query = this._query;
      if (!query) return;

      const url = `${window.Shopify.routes.root}search?type=product&q=${encodeURIComponent(query)}&section_id=${SECTION_ID}`;

      try {
        const response = await fetch(url, { priority: 'low' });
        if (!response.ok) return;

        const document_ = new DOMParser().parseFromString(await response.text(), 'text/html');
        const rendered = document_.querySelector('[data-recently-viewed-search]');
        if (!rendered || rendered.childElementCount === 0) return;

        this.replaceChildren(...window.document.importNode(rendered, true).childNodes);
        this.hidden = false;
      } catch (e) {
        // Offline, or a product that has since been unpublished — the drawer
        // just keeps the state it had.
      }
    }
  }

  if (!window.customElements.get('recently-viewed-search')) {
    window.customElements.define('recently-viewed-search', RecentlyViewedSearch);
  }
}
